import * as React from 'react';
import { cn } from '@/lib/utils';
import type { ControlledOption } from './intake-options';

/**
 * The shared field primitives for the intake wizard.
 *
 * These carry no user-facing text of their own — every label, option label and
 * marker word is passed in by the caller, already resolved from the i18next
 * catalogue (R18.9). That is what lets them live under `features/**` without
 * tripping the source-literal lint rule, and what keeps the wizard's copy in one
 * place.
 */

/** A labelled field row: label above the control, optional required marker and hint. */
export function Field({
  label,
  htmlFor,
  required = false,
  requiredMark,
  hint,
  className,
  children,
}: {
  readonly label: string;
  readonly htmlFor?: string;
  readonly required?: boolean;
  /** The already-resolved "required" marker glyph/word; caller supplies for i18n. */
  readonly requiredMark?: string;
  readonly hint?: string;
  /** Additional classes for the row (e.g. a grid span in a caller's layout). */
  readonly className?: string;
  readonly children: React.ReactNode;
}): React.JSX.Element {
  return (
    <div className={cn('mb-3 flex flex-col gap-1', className)}>
      <label htmlFor={htmlFor} className="text-[11.5px] font-semibold text-txt">
        {label}
        {required && requiredMark ? (
          <span className="ml-1 text-[var(--warn)]" aria-hidden>
            {requiredMark}
          </span>
        ) : null}
      </label>
      {children}
      {hint ? <p className="text-muted-foreground text-[11px]">{hint}</p> : null}
    </div>
  );
}

/**
 * The conditional-disclosure block (UI spec §6.1): 3px accent left border,
 * `#FAFCFE` background, radius `0 4px 4px 0`, padding `10px 12px`. Used for the
 * KAM/IND named-customer block, the DTE/CSC dependency blocks and the Field Force
 * role blocks.
 */
export function ConditionalBlock({
  children,
  className,
  ...rest
}: React.ComponentProps<'div'>): React.JSX.Element {
  return (
    <div
      data-slot="cond-block"
      className={cn(
        'rounded-[0_4px_4px_0] border-l-[3px] border-[var(--accent)] bg-[#FAFCFE] px-3 py-[10px]',
        className,
      )}
      {...rest}
    >
      {children}
    </div>
  );
}

/**
 * The form-control class these primitives apply: 8px-tall control, 12.5px type and
 * the accent focus ring of the MAINTAIN form vocabulary.
 *
 * Exported for the one control this module cannot wrap — a plain `<select>` whose
 * options are not `ControlledOption`s, as the Manpower editors have — so it looks
 * identical to a {@link TextInput} beside it instead of carrying a hand-copied
 * class string. Distinct from `FILTER_CONTROL_CLASS` (UI spec §3.6) on purpose: a
 * filter strip control is 12px with tighter padding, a form control is 12.5px.
 */
export const FORM_CONTROL_CLASS =
  'border-line h-8 rounded-[var(--radius-control)] border bg-panel px-2 text-[12.5px] text-txt outline-none focus-visible:border-[var(--accent)]';

/** A plain text input bound by the caller (register or Controller). */
export const TextInput = React.forwardRef<HTMLInputElement, React.ComponentProps<'input'>>(
  function TextInput({ className, type, ...props }, ref) {
    return (
      <input
        ref={ref}
        type={type}
        className={cn(
          'border-line h-8 rounded-[var(--radius-control)] border bg-panel px-2 text-[12.5px] text-txt outline-none focus-visible:border-[var(--accent)]',
          type === 'date' && 'w-[120px]',
          className,
        )}
        {...props}
      />
    );
  },
);

/** A multi-line text input. */
export const TextArea = React.forwardRef<HTMLTextAreaElement, React.ComponentProps<'textarea'>>(
  function TextArea({ className, ...props }, ref) {
    return (
      <textarea
        ref={ref}
        className={cn(
          'border-line min-h-[68px] rounded-[var(--radius-control)] border bg-panel px-2 py-1.5 text-[12.5px] text-txt outline-none focus-visible:border-[var(--accent)]',
          className,
        )}
        {...props}
      />
    );
  },
);

/**
 * A single-select of controlled values. The `inactiveMarker` is the already
 * resolved marker text the caller passes; a retained-inactive value (R5.9) shows
 * it appended to its label so a deactivated-but-referenced value is visibly
 * flagged rather than silently present.
 */
export const ControlledSelect = React.forwardRef<
  HTMLSelectElement,
  React.ComponentProps<'select'> & {
    readonly options: readonly ControlledOption[];
    readonly placeholder: string;
    readonly inactiveMarker: string;
  }
>(function ControlledSelect({ options, placeholder, inactiveMarker, className, ...props }, ref) {
  return (
    <select
      ref={ref}
      className={cn(
        'border-line h-8 rounded-[var(--radius-control)] border bg-panel px-2 text-[12.5px] text-txt outline-none focus-visible:border-[var(--accent)]',
        className,
      )}
      {...props}
    >
      <option value="">{placeholder}</option>
      {options.map((option) => (
        <option key={option.id} value={option.id}>
          {option.isInactive ? `${option.label} ${inactiveMarker}` : option.label}
        </option>
      ))}
    </select>
  );
});

/**
 * A checkbox group over controlled values (multi-select), controlled by the
 * caller through `selected`/`onToggle`. Each retained-inactive option shows the
 * caller's inactive marker (R5.9).
 *
 * Without `columns`, options sit inline at their own width with a fixed gap and
 * wrap onto the next line, so each label stays next to its checkbox and the
 * group does not stretch across a wide screen. An option
 * with a `colorHex` (Platforms) shows a colour dot, and a checked one becomes a
 * chip tinted in that colour, matching the Calendar_View bars and legend.
 *
 * With `single`, the options are radio buttons: picking one reports it as
 * checked, and the caller replaces the selection (Platform).
 */
export function CheckboxGroup({
  options,
  selected,
  onToggle,
  inactiveMarker,
  legend,
  columns,
  single = false,
}: {
  readonly options: readonly ControlledOption[];
  readonly selected: readonly string[];
  readonly onToggle: (id: string, checked: boolean) => void;
  readonly inactiveMarker: string;
  readonly legend: string;
  readonly columns?: number;
  readonly single?: boolean;
}): React.JSX.Element {
  const selectedSet = new Set(selected);
  const radioName = React.useId();
  return (
    <fieldset className="border-0 p-0">
      <legend className="sr-only">{legend}</legend>
      <div
        className={columns === undefined ? 'flex flex-wrap gap-x-2 gap-y-0.5' : 'grid gap-x-3 gap-y-0.5'}
        style={
          columns === undefined
            ? undefined
            : { gridTemplateColumns: `repeat(${String(columns)}, minmax(0, 1fr))` }
        }
      >
        {options.map((option) => {
          const checked = selectedSet.has(option.id);
          const colour = option.colorHex;
          return (
            <label
              key={option.id}
              className="flex min-w-0 cursor-pointer items-center gap-2 rounded-[var(--radius-control)] border border-transparent px-1.5 py-[3px] text-[12px] text-txt hover:bg-[var(--surface-alt)]"
              style={
                colour && checked
                  ? { backgroundColor: `${colour}1f`, borderColor: `${colour}80` }
                  : undefined
              }
            >
              <input
                type={single ? 'radio' : 'checkbox'}
                {...(single ? { name: radioName } : {})}
                checked={checked}
                onChange={(event) => onToggle(option.id, event.target.checked)}
                style={colour ? { accentColor: colour } : undefined}
              />
              {colour ? (
                <span
                  aria-hidden
                  className="inline-block size-2.5 shrink-0 rounded-full"
                  style={{ backgroundColor: colour }}
                />
              ) : null}
              <span className="truncate" title={option.label}>
                {option.label}
                {option.isInactive ? <span className="ml-1 text-muted-foreground">{inactiveMarker}</span> : null}
              </span>
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}

/** A yes/no toggle rendered as two radio-like buttons. */
export function YesNoToggle({
  value,
  onChange,
  yesLabel,
  noLabel,
  name,
}: {
  readonly value: boolean;
  readonly onChange: (next: boolean) => void;
  readonly yesLabel: string;
  readonly noLabel: string;
  readonly name: string;
}): React.JSX.Element {
  return (
    <div role="radiogroup" aria-label={name} className="border-line inline-flex overflow-hidden rounded-[var(--radius-control)] border">
      {[
        { on: true, label: yesLabel },
        { on: false, label: noLabel },
      ].map((choice) => {
        const active = choice.on === value;
        return (
          <button
            key={choice.label}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onChange(choice.on)}
            className={cn(
              'px-3 py-[5px] text-[12px] font-semibold transition-colors',
              active
                ? 'bg-[var(--accent-soft)] text-[var(--accent-hover)]'
                : 'bg-panel text-muted-foreground hover:bg-surface-alt',
            )}
          >
            {choice.label}
          </button>
        );
      })}
    </div>
  );
}

/** The manual-override marker badge (UI spec §3.4 `badge-ovr`: `#FDF0DC` / `#9A5B00`). */
export function OverrideBadge({ children }: { readonly children: React.ReactNode }): React.JSX.Element {
  return (
    <span className="ml-2 inline-flex items-center rounded-[var(--radius-badge)] bg-[#FDF0DC] px-1.5 py-0.5 text-[10.5px] font-semibold text-[#9A5B00]">
      {children}
    </span>
  );
}
